import config from 'constants/config'
import React from 'react'
import { useIntl } from 'react-intl'
import * as Analytics from 'analytics/analytics'
import Label from './Label'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_BUNDLE_AND_SAVE,
  OFFER_TYPE_VILLA,
} from 'constants/offer'
import {
  EXPERIENCE_PRODUCT_TYPE_ALWAYS_ON,
  EXPERIENCE_PRODUCT_TYPE_CHANNEL_MANAGER,
  EXPERIENCE_PRODUCT_TYPE_FLASH,
} from 'constants/experience'
import UltraLuxLogo from './UltraLuxLogo'
import { DIRECT_TOUR, PARTNER_TOUR, ULTRA_LUX_TOUR } from 'constants/tours'
import { LUXURY_ESCAPES } from 'constants/brands'
import {
  FLASH_HOTEL_PRODUCT_LABEL,
  LE_EXCLUSIVE,
  LE_FLASH_HOTEL_PRODUCT_LABEL,
  LE_PARTNER,
  LE_RENTAL_PRODUCT_LABEL,
  LME_HOTEL_PRODUCT_LABEL,
  LPP_PRODUCT_LABEL,
  LUX_BUNDLE_LABEL,
  RENTAL_PRODUCT_LABEL,
  TAO_HOTEL_PRODUCT_LABEL,
} from 'constants/productLabel'
import SolidFireIcon from '../Icons/solid/SolidFireIcon'
import SolidStarCircleIcon from '../Icons/solid/SolidStarCircleIcon'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import styled from 'styled-components'
import clsx from 'clsx'

const LabelWithEvents = styled(Label)`
  &.with-events {
    pointer-events: auto;
  }
`

type Variant = 'limited-time-exclusive' |
  'premium' |
  'last-minute' |
  'partner' |
  'partner-property' |
  'experiences' |
  'exclusive' |
  'ultralux' |
  'bundle' |
  'limited-time-special' |
  'corporate' |
  'rental'

interface Config extends Omit<React.ComponentProps<typeof Label>, 'children'>, Utils.PropsWithDataAttributes {
  children: (brand: App.BrandName) => React.ReactNode | undefined
}

/**
 * Mapping application product types to the variants
 * Variants are the names design refer to these as
 * so to keep this in sync with design - we keep the variant names
 * but hide the implementation away in this component
 */

const offerTypeToProductMap: Partial<Record<App.ProductType, Variant>> = {
  [OFFER_TYPE_HOTEL]: 'limited-time-exclusive',
  [OFFER_TYPE_LAST_MINUTE]: 'last-minute',
  [OFFER_TYPE_ALWAYS_ON]: 'premium',
  [OFFER_TYPE_BED_BANK]: 'partner-property',
  [ULTRA_LUX_TOUR]: 'ultralux',
  [DIRECT_TOUR]: 'exclusive',
  [PARTNER_TOUR]: 'partner',
  [EXPERIENCE_PRODUCT_TYPE_ALWAYS_ON]: 'experiences',
  [EXPERIENCE_PRODUCT_TYPE_FLASH]: 'exclusive',
  [EXPERIENCE_PRODUCT_TYPE_CHANNEL_MANAGER]: 'experiences',
  ultralux_hotel: 'ultralux',
  [OFFER_TYPE_BUNDLE_AND_SAVE]: 'bundle',
  [OFFER_TYPE_VILLA]: 'rental',
  limited_time_special: 'limited-time-special',
  corporate: 'corporate',
  'cruise-flash': 'limited-time-exclusive',
  'cruise-tactical': 'premium',
  'cruise-exclusive': 'exclusive',
}

const variantConfig: Record<Variant, Config> = {
  'limited-time-exclusive': {
    kind: 'primary',
    variant: 'exclusive',
    startIcon: <SolidStarCircleIcon />,
    children: brand => brand === LUXURY_ESCAPES ? LE_FLASH_HOTEL_PRODUCT_LABEL : FLASH_HOTEL_PRODUCT_LABEL,
  },
  premium: {
    kind: 'secondary',
    variant: 'dark',
    children: brand => brand === LUXURY_ESCAPES ? null : TAO_HOTEL_PRODUCT_LABEL,
  },
  'last-minute': {
    kind: 'secondary',
    variant: 'urgency',
    children: () => LME_HOTEL_PRODUCT_LABEL,
  },
  'partner-property': {
    kind: 'secondary',
    variant: 'grey',
    children: brand => brand === LUXURY_ESCAPES ? null : LPP_PRODUCT_LABEL,
  },
  partner: {
    kind: 'secondary',
    variant: 'grey',
    children: brand => brand === LUXURY_ESCAPES ? LE_PARTNER : null,
  },
  experiences: {
    kind: 'secondary',
    variant: 'dark',
    children: () => undefined,
  },
  exclusive: {
    kind: 'primary',
    variant: 'dark',
    children: brand => brand === LUXURY_ESCAPES ? LE_EXCLUSIVE : null,
  },
  ultralux: {
    kind: 'secondary',
    variant: 'ghost',
    children: () => <UltraLuxLogo height={10} />,
    'data-testid': 'ultra-lux-label',
  },
  bundle: {
    kind: 'primary',
    variant: 'highlight',
    children: () => LUX_BUNDLE_LABEL,
  },
  'limited-time-special': {
    kind: 'primary',
    variant: isLPCTacticalEnabled() ? 'urgency' : 'highlight-secondary',
    startIcon: <SolidFireIcon />,
    children: () => undefined,
  },
  corporate: {
    kind: 'primary',
    variant: 'highlight-secondary',
    startIcon: <SolidStarCircleIcon />,
    children: () => undefined,
  },
  rental: {
    kind: 'secondary',
    variant: 'dark',
    children: brand => brand === LUXURY_ESCAPES ? LE_RENTAL_PRODUCT_LABEL : RENTAL_PRODUCT_LABEL,
  },
}

interface Props {
  productType: App.ProductType
  className?: string
  kind?: 'default' | 'plain' | 'hidden'
}

function ProductTypeLabel(props: Props) {
  const { productType, kind = 'default', className, ...rest } = props
  const intl = useIntl()
  const variant = offerTypeToProductMap[productType]

  if (!variant || kind === 'hidden') {
    return null
  }

  const i18nOverrides: Partial<Record<Variant, string>> = {
    experiences: intl.formatMessage({
      id: 'bH4RutGrNK',
      defaultMessage: 'LUX Collection',
      description: 'Product type label for experiences on the experience offer or booking page',
    }),
    'limited-time-special': intl.formatMessage({
      id: 'G05SQ0bpd5',
      defaultMessage: 'Limited time special',
      description: 'Product type label for limited time special offers',
    }),
    corporate: intl.formatMessage({
      id: 'dcstPnhEV/',
      defaultMessage: 'Business Traveller Special',
      description: 'Product type label for corporate/business traveller offers',
    }),
  }

  const labelConfig = variantConfig[variant]
  const content = i18nOverrides[variant] ?? labelConfig.children(config.BRAND)
  if (!content) {
    return null
  }

  const withEvents = variant === 'limited-time-exclusive'

  const handleHover = () => {
    Analytics.trackClientEvent({
      subject: 'ltle_tag_hover',
      action: 'hover',
      category: 'flash_label',
      type: 'interaction',
    })
  }

  const handleClick = () => {
    Analytics.trackClientEvent({
      subject: 'ltle_tag_click',
      action: 'click',
      category: 'flash_label',
      type: 'interaction',
    })
  }

  const eventHandlers = withEvents
    ? {
        onMouseEnter: handleHover,
        onClick: handleClick,
      }
    : {}

  return <LabelWithEvents
    {...rest}
    {...labelConfig}
    {...eventHandlers}
    className={clsx(className, { 'with-events': withEvents })}
    kind={kind === 'default' ? labelConfig.kind : 'plain'}
  >
    {content}
  </LabelWithEvents>
}

export default ProductTypeLabel
